/**
 * Phase 3 mandate and payment-provider domain types.
 */

export const PAYMENT_PROVIDERS = ['mock', 'transactpay'] as const;
export type PaymentProviderCode = (typeof PAYMENT_PROVIDERS)[number];

export const PROVIDER_ORDER_PURPOSES = ['MANDATE_SETUP', 'INSTALLMENT_PAYMENT'] as const;
export type ProviderOrderPurpose = (typeof PROVIDER_ORDER_PURPOSES)[number];

export const PROVIDER_ORDER_STATUSES = ['PENDING', 'CREATED', 'COMPLETED', 'FAILED'] as const;
export type ProviderOrderStatus = (typeof PROVIDER_ORDER_STATUSES)[number];

export const MANDATE_INTERNAL_STATUSES = [
  'CREATING_ORDER',
  'ORDER_CREATED',
  'CREATING_MANDATE',
  'PENDING_ACTIVATION_PAYMENT',
  'PENDING_PROVIDER_AUTHORIZATION',
  'ACTIVE',
  'FAILED',
  'EXPIRED',
  'CANCELLATION_REQUESTED',
  'CANCELLATION_IN_PROGRESS',
  'CANCELLED',
] as const;
export type MandateInternalStatus = (typeof MANDATE_INTERNAL_STATUSES)[number];

export const MANDATE_PROVIDER_STATUSES = [
  'PENDING',
  'PENDING_AUTHORIZATION',
  'ACTIVE',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
] as const;
export type MandateProviderStatus = (typeof MANDATE_PROVIDER_STATUSES)[number];

export const MANDATE_STATUS_SOURCES = [
  'customer',
  'staff',
  'system_poll',
  'mock_provider',
  'provider_callback_future',
] as const;
export type MandateStatusSource = (typeof MANDATE_STATUS_SOURCES)[number];

export const MANDATE_CANCELLATION_STATUSES = [
  'REQUESTED',
  'UNDER_REVIEW',
  'SUBMITTED_TO_PROVIDER',
  'CONFIRMED',
  'REJECTED',
  'CANCELLED',
] as const;
export type MandateCancellationStatus = (typeof MANDATE_CANCELLATION_STATUSES)[number];

export const MOCK_MANDATE_SCENARIOS = [
  'success',
  'pending',
  'activation_failed',
  'provider_unavailable',
  'unsupported_bank',
  'invalid_account',
  'cancelled',
  'expired',
] as const;
export type MockMandateScenario = (typeof MOCK_MANDATE_SCENARIOS)[number];

export interface SupportedBankView {
  bankCode: string;
  bankName: string;
  provider: PaymentProviderCode;
  status: 'ACTIVE' | 'INACTIVE';
  lastSynchronizedAt: string;
}

export interface MandateActivationInstructionsView {
  activationAmount: string;
  activationBankName: string;
  /** Full account number — customer-only while authorization is pending. */
  activationRecipientAccount?: string;
  activationRecipientAccountMasked: string;
  expectedAuthorizationHoursMin: number;
  expectedAuthorizationHoursMax: number;
}

export interface MandateSummaryView {
  id: string;
  subscriptionId: string;
  customerId: string;
  bankAccountId: string;
  provider: PaymentProviderCode;
  mandateCode: string | null;
  directDebitId: string | null;
  internalStatus: MandateInternalStatus;
  providerStatus: MandateProviderStatus | null;
  activationAmount: string | null;
  activationBankName: string | null;
  activationRecipientAccountMasked: string | null;
  activationInstructions?: MandateActivationInstructionsView;
  createdAt: string;
  authorizationPaymentDeclaredAt: string | null;
  activatedAt: string | null;
  failedAt: string | null;
  expiredAt: string | null;
  cancelledAt: string | null;
  lastStatusCheckAt: string | null;
  nextStatusCheckAt: string | null;
  statusCheckCount: number;
  failureCode: string | null;
  failureReason: string | null;
}
